import { createContext, useContext } from 'react';
import type { Module, SignalRow } from '../../api';

/**
 * Text scale for a chart drawn narrower than the A4 page it was laid out for:
 * the module pages on a phone show these 600-wide charts at about half size.
 */
export const ChartTextScale = createContext(1);
/** On a module page, a chart with nothing to draw draws nothing rather than saying so. */
export const ChartHideEmpty = createContext(false);

/**
 * One chart per module, drawn from that module's own rows.
 *
 * The handoff ships twenty-one bespoke charts with their figures written into
 * them. Those figures would be a second copy of the report — free to disagree
 * with the rows printed directly beneath them on the same page. So the shapes
 * are kept and the numbers are not: each chart reads the rows it sits under,
 * which is the one thing that cannot contradict them.
 *
 * Where a figure is missing the axis is still drawn and the mark omitted,
 * rather than plotting a zero that would read as a measurement.
 */

/**
 * Layout width. The PDF lays charts out at 600; a module page on a phone lays
 * the same chart out at its own width so the text stays readable. Set by
 * ChartFor, which is the only entry point, before its chart renders.
 */
let W = 600;
const H = 168;
const PAD = 26;
const INK = '#1F1A1F';
const MUTE = 'rgba(31,26,31,0.45)';
const GRID = 'rgba(31,26,31,0.12)';

/** Print tones. Full-opacity hex throughout: an alpha tint renders as a blob. */
export const DOC_TONE: Record<string, string> = {
  strong: '#1E8A5A',
  ok: '#3A3BD6',
  warn: '#C9741A',
  high: '#D63A2E',
  neutral: '#4A4149',
  cyan: '#2BB3E6',
  pink: '#E22A8C',
};

export const toneOf = (t: string | undefined): string => DOC_TONE[t ?? 'neutral'] ?? DOC_TONE.neutral;

/**
 * A row's value as a number, and the unit it is in — but only where the value
 * really is a measurement.
 *
 * "2 or 3 storey mix — Low-medium density residential" contains the digit 2,
 * and an earlier version charted it as a bar of length two. A value has to be
 * a figure with at most a short unit on it; a sentence that happens to contain
 * a number is prose, and prose has no length.
 */
const MEASUREMENT = /^[+$−-]?\s*\$?[\d,]+(?:\.\d+)?\s*(?:m²|%|\/\s*100|\/\s*10|km|m|k|days?|yrs?|hrs?|of\s+\d+)?$/i;

export function figureOf(v: string | null | undefined): number | null {
  return measure(v)?.n ?? null;
}

export function measure(v: string | null | undefined): { n: number; unit: string } | null {
  if (!v || v === '—') return null;
  const s = v.replace(/,/g, '').trim();
  if (!MEASUREMENT.test(s)) return null;
  const m = /(-?\d+(?:\.\d+)?)/.exec(s);
  if (!m) return null;
  let n = Number(m[1]);
  if (!Number.isFinite(n)) return null;
  // A unit, so two rows are only ever charted against each other when they
  // measure the same kind of thing.
  const unit = /%/.test(s)
    ? '%'
    : /m²/.test(s)
      ? 'm²'
      : /^\$|\$/.test(s)
        ? '$'
        : /\/\s*10{1,2}\b/.test(s)
          ? '/100'
          : /\bkm\b/i.test(s)
            ? 'km'
            : /\bdays?\b/i.test(s)
              ? 'days'
              : /\byrs?\b/i.test(s)
                ? 'yrs'
                : /\d\s*m\b/i.test(s)
                  ? 'm'
                  : 'count';
  // Scores shown out of 10 are charted on the same 0–100 scale as before.
  if (unit === '/100' && /\/\s*10\b/.test(s)) n *= 10;
  if (unit === '$' && /\dm\b/i.test(s)) n *= 1_000_000;
  else if (unit === '$' && /\dk\b/i.test(s)) n *= 1_000;
  if (/^[−-]/.test(s)) n = -Math.abs(n);
  return { n, unit };
}

const short = (n: number): string =>
  Math.abs(n) >= 1_000_000
    ? `$${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}m`
    : Math.abs(n) >= 1_000
      ? `$${Math.round(n / 1000)}k`
      : String(Math.round(n * 10) / 10);

const clip = (s: string, n: number): string => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** Break a label into at most two lines of n characters, by word; only an overlong second line is clipped. */
const twoLines = (s: string, n: number): [string, string] => {
  if (s.length <= n) return [s, ''];
  const words = s.split(/\s+/);
  let a = '';
  while (words.length && (a ? `${a} ${words[0]}` : words[0]).length <= n) a = a ? `${a} ${words.shift()}` : (words.shift() as string);
  if (!a) return [clip(s, n), ''];
  return [a, clip(words.join(' '), n)];
};

/** Characters of `size` text (Archivo, at the chart's text scale) that fit across `px`. */
const fits = (px: number, size: number, k = 1): number => Math.max(4, Math.floor(px / (size * k * 0.56)));
/** A narrow layout — a module page on a phone rather than the A4 page. */
const narrow = () => W < 460;

function Text({
  x, y, children, fill = MUTE, size = 8, weight = 600, anchor = 'start',
}: {
  x: number; y: number; children: string; fill?: string; size?: number; weight?: number;
  anchor?: 'start' | 'middle' | 'end';
}) {
  const k = useContext(ChartTextScale);
  return (
    <text x={x} y={y} fill={fill} fontSize={size * k} fontWeight={weight} fontFamily="Archivo, sans-serif" textAnchor={anchor}>
      {children}
    </text>
  );
}

function Frame({ children, height = H }: { children: React.ReactNode; height?: number }) {
  return (
    <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} style={{ display: 'block', overflow: 'visible' }}>
      <Depth />
      {children}
    </svg>
  );
}

/**
 * The 3D look on the module pages, as one lighting filter: a specular bevel
 * lit from the top-left, a darker copy dropped beneath for an extruded edge,
 * and a soft shadow. Applied by CSS (.rb-chart-3d) to filled shapes only, so
 * text, gridlines and outlines stay crisp and the PDF stays flat for print.
 */
function Depth() {
  return (
    <defs>
      <filter id="rb3d" x="-25%" y="-25%" width="150%" height="170%" colorInterpolationFilters="sRGB">
        <feGaussianBlur in="SourceAlpha" stdDeviation="1.4" result="blur" />
        <feSpecularLighting in="blur" surfaceScale="3.2" specularConstant="0.85" specularExponent="16" lightingColor="#FFFFFF" result="spec">
          <fePointLight x="-300" y="-500" z="420" />
        </feSpecularLighting>
        <feComposite in="spec" in2="SourceAlpha" operator="in" result="specIn" />
        <feComposite in="SourceGraphic" in2="specIn" operator="arithmetic" k1="0" k2="1" k3="0.55" k4="0" result="lit" />
        <feOffset in="SourceGraphic" dx="0" dy="2.4" result="drop" />
        <feColorMatrix in="drop" type="matrix" values="0.55 0 0 0 0  0 0.55 0 0 0  0 0 0.55 0 0  0 0 0 1 0" result="edge" />
        <feGaussianBlur in="SourceAlpha" stdDeviation="2.6" result="sb" />
        <feOffset in="sb" dy="4.5" result="so" />
        <feFlood floodColor="#1F1A1F" floodOpacity="0.2" />
        <feComposite in2="so" operator="in" result="shadow" />
        <feMerge>
          <feMergeNode in="shadow" />
          <feMergeNode in="edge" />
          <feMergeNode in="lit" />
        </feMerge>
      </filter>
    </defs>
  );
}

const gridLines = (n: number, y0: number, y1: number) =>
  Array.from({ length: n }, (_, i) => {
    const y = y0 + ((y1 - y0) * i) / (n - 1);
    return <line key={`g${i}`} x1={PAD} y1={y} x2={W - PAD} y2={y} stroke={GRID} strokeWidth={1} />;
  });

/** Rows that carry a real measurement, with the unit it is in. */
const measurable = (rows: SignalRow[]): { row: SignalRow; n: number; unit: string }[] =>
  rows
    .map((row) => {
      const m = measure(row.v);
      return m ? { row, n: m.n, unit: m.unit } : null;
    })
    .filter((x): x is { row: SignalRow; n: number; unit: string } => x != null);

/**
 * The largest set of rows that measure the same kind of thing.
 *
 * Bedrooms, a lot size and three tenure percentages on one axis scaled by the
 * largest of them leaves the bedrooms an invisible sliver and says nothing.
 * One chart, one unit.
 */
function sameUnit(rows: SignalRow[]): { row: SignalRow; n: number; unit: string }[] {
  const all = measurable(rows);
  const groups = new Map<string, typeof all>();
  for (const item of all) groups.set(item.unit, [...(groups.get(item.unit) ?? []), item]);
  let best: typeof all = [];
  for (const g of groups.values()) if (g.length > best.length) best = g;
  return best;
}

function Empty({ reason }: { reason: string }) {
  if (useContext(ChartHideEmpty)) return null;
  return (
    <Frame height={96}>
      <line x1={PAD} y1={72} x2={W - PAD} y2={72} stroke={GRID} />
      <line x1={PAD} y1={20} x2={PAD} y2={72} stroke={GRID} />
      <Text x={W / 2} y={46} size={9} anchor="middle">{reason}</Text>
    </Frame>
  );
}

function Bars({ rows, limit = 8 }: { rows: SignalRow[]; limit?: number }) {
  const items = sameUnit(rows).slice(0, limit);
  if (items.length === 0) return <Empty reason="No figure on this page can be charted." />;
  const max = Math.max(...items.map((i) => Math.abs(i.n)));
  const x0 = PAD + 150;
  const x1 = W - PAD - 58;
  const step = (H - 26) / items.length;
  return (
    <Frame>
      <line x1={x0} y1={10} x2={x0} y2={H - 12} stroke={GRID} />
      {items.map(({ row, n }, i) => {
        const y = 14 + i * step;
        const w = max > 0 ? ((x1 - x0) * Math.abs(n)) / max : 0;
        const col = toneOf(row.tone);
        return (
          <g key={row.k + i}>
            <Text x={PAD} y={y + 8} fill={INK} size={9} weight={500}>{clip(row.k, 26)}</Text>
            <rect x={x0} y={y} width={Math.max(w, 1)} height={9} rx={1.5} fill={col} />
            <Text x={x0 + w + 8} y={y + 8} fill={col} size={8.5}>{row.v}</Text>
          </g>
        );
      })}
    </Frame>
  );
}


/**
 * A dial, for a hero that is genuinely a score out of 100.
 *
 * It used to take any hero: Rent & Yield's "$850 / WK" filled the ring to the
 * brim, because 850 clamped to 100%. A dial of a number that has no ceiling
 * says nothing, so the ring is only drawn when there is a scale to draw it on.
 */
function Gauge({ module }: { module: Module }) {
  const outOf10 = /\/\s*10\b/.test(module.hero.unit);
  const scored = outOf10 || /\/\s*100/.test(module.hero.unit) || /\/\s*100/.test(module.hero.value);
  const fig = figureOf(module.hero.value);
  const raw = fig != null && outOf10 ? fig * 10 : fig;
  const value = scored && raw != null && raw >= 0 && raw <= 100 ? raw : null;
  const parts = sameUnit(module.rows).slice(0, 5);
  const cx = PAD + 74;
  const cy = H / 2;
  const r = 52;
  const col = toneOf(module.hero.tone);
  const pct = value == null ? null : Math.max(0, Math.min(1, value / 100));
  const circumference = 2 * Math.PI * r;
  const max = Math.max(...parts.map((p) => Math.abs(p.n)), 1);
  return (
    <Frame>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={GRID} strokeWidth={9} />
      {pct != null && (
        <circle
          cx={cx} cy={cy} r={r} fill="none" stroke={col} strokeWidth={9} strokeLinecap="round"
          strokeDasharray={`${circumference * pct} ${circumference}`}
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      )}
      <Text x={cx} y={cy + 2} fill={INK} size={22} weight={700} anchor="middle">{module.hero.value}</Text>
      <Text x={cx} y={cy + 16} size={7.5} anchor="middle">{module.hero.unit || module.hero.label}</Text>
      {parts.map(({ row, n }, i) => {
        const y = 26 + i * 24;
        const bx = PAD + 168;
        const bw = W - PAD - 70 - bx;
        return (
          <g key={row.k + i}>
            <Text x={bx} y={y - 4} fill={INK} size={8.5} weight={500}>{clip(row.k, 30)}</Text>
            <rect x={bx} y={y} width={bw} height={5} rx={2.5} fill={GRID} />
            <rect x={bx} y={y} width={Math.max((bw * Math.abs(n)) / max, 2)} height={5} rx={2.5} fill={toneOf(row.tone)} />
            <Text x={W - PAD - 56} y={y + 5} fill={toneOf(row.tone)} size={8}>{row.v}</Text>
          </g>
        );
      })}
    </Frame>
  );
}

/** Comparable sales: what each sold for, against how far away it is. */
function Scatter({ rows, midpoint }: { rows: SignalRow[]; midpoint: number | null }) {
  const pts = rows
    .map((row) => {
      const price = figureOf(row.v);
      const km = /([\d.]+)\s*km/.exec(row.s ?? '');
      return price != null && km ? { row, price, km: Number(km[1]) } : null;
    })
    .filter((p): p is { row: SignalRow; price: number; km: number } => p != null);
  if (pts.length < 2) return <Bars rows={rows} />;

  const prices = pts.map((p) => p.price);
  const lo = Math.min(...prices, ...(midpoint != null ? [midpoint] : []));
  const hi = Math.max(...prices, ...(midpoint != null ? [midpoint] : []));
  const maxKm = Math.max(...pts.map((p) => p.km), 1);
  const x0 = PAD + 46;
  const x1 = W - PAD - 20;
  const y0 = 18;
  const y1 = H - 30;
  const sx = (km: number) => x0 + ((x1 - x0) * km) / maxKm;
  const sy = (v: number) => y1 - ((y1 - y0) * (v - lo)) / (hi - lo || 1);
  return (
    <Frame>
      {gridLines(4, y0, y1)}
      {midpoint != null && (
        <>
          <line x1={x0} y1={sy(midpoint)} x2={x1} y2={sy(midpoint)} stroke={DOC_TONE.high} strokeWidth={1.2} strokeDasharray="4 3" />
          <Text x={x1} y={sy(midpoint) - 5} fill={DOC_TONE.high} size={8} anchor="end">{`MIDPOINT ${short(midpoint)}`}</Text>
        </>
      )}
      {pts.map((p, i) => (
        <circle key={i} cx={sx(p.km)} cy={sy(p.price)} r={4} fill={DOC_TONE.ok} opacity={0.75} />
      ))}
      <Text x={PAD} y={sy(hi) + 3} size={7.5}>{short(hi)}</Text>
      <Text x={PAD} y={sy(lo) + 3} size={7.5}>{short(lo)}</Text>
      <Text x={x0} y={H - 12} size={7.5}>0 km</Text>
      <Text x={x1} y={H - 12} size={7.5} anchor="end">{`${maxKm.toFixed(1)} km`}</Text>
    </Frame>
  );
}

/** The seven liveability scores, as the design's radar. */
function Radar({ rows }: { rows: SignalRow[] }) {
  const items = sameUnit(rows).filter(({ n }) => n >= 0 && n <= 100).slice(0, 7);
  if (items.length < 3) return <Bars rows={rows} />;
  const cx = W / 2;
  const cy = H / 2 + 4;
  const r = 58;
  const at = (i: number, frac: number) => {
    const a = (Math.PI * 2 * i) / items.length - Math.PI / 2;
    return [cx + Math.cos(a) * r * frac, cy + Math.sin(a) * r * frac] as const;
  };
  const ring = (frac: number) => items.map((_, i) => at(i, frac).join(',')).join(' ');
  const shape = items.map(({ n }, i) => at(i, n / 100).join(',')).join(' ');
  return (
    <Frame>
      {[0.25, 0.5, 0.75, 1].map((f) => (
        <polygon key={f} points={ring(f)} fill="none" stroke={GRID} strokeWidth={1} />
      ))}
      <polygon points={shape} fill={DOC_TONE.ok} fillOpacity={0.16} stroke={DOC_TONE.ok} strokeWidth={1.6} />
      {items.map(({ row, n }, i) => {
        const [lx, ly] = at(i, 1.24);
        return (
          <Text key={row.k} x={lx} y={ly + 3} size={7.5} anchor={lx > cx + 4 ? 'start' : lx < cx - 4 ? 'end' : 'middle'}>
            {`${clip(row.k, 12)} ${n}`}
          </Text>
        );
      })}
    </Frame>
  );
}

// ── Bespoke forms, one per module ──────────────────────────────────────────
// Each reads the rows printed beneath it on the same page. Where a figure is
// absent the axis is still drawn and the mark left off, rather than a zero
// standing in for a measurement.

const rowBy = (rows: SignalRow[], re: RegExp) => rows.find((r) => re.test(r.k));
const rowsBy = (rows: SignalRow[], re: RegExp) => rows.filter((r) => re.test(r.k));

/** 03 · what was asked for, met and unmet, as blocks. */
function FitBlocks({ rows }: { rows: SignalRow[] }) {
  const answers = rows.filter((r) => ['Met', 'Not met', 'Not published'].includes(r.v));
  if (answers.length === 0) return <Empty reason="No assessment was recorded for this report." />;
  const k = useContext(ChartTextScale);
  const cols = Math.min(answers.length, narrow() ? 4 : 7);
  const bw = (W - PAD * 2 - (cols - 1) * 10) / cols;
  const shown = answers.slice(0, 14);
  const ROW = 72;
  return (
    <Frame height={16 + Math.ceil(shown.length / cols) * ROW}>
      {shown.map((r, i) => {
        const x = PAD + (i % cols) * (bw + 10);
        const y = 16 + Math.floor(i / cols) * ROW;
        const [l1, l2] = twoLines(r.k, fits(bw + 6, 7, k));
        const col = r.v === 'Met' ? DOC_TONE.strong : r.v === 'Not met' ? DOC_TONE.warn : DOC_TONE.neutral;
        return (
          <g key={r.k + i}>
            <rect x={x} y={y} width={bw} height={30} rx={4} fill={col} opacity={r.v === 'Not published' ? 0.18 : 0.9} />
            <Text x={x + bw / 2} y={y + 44} size={7} anchor="middle">{l1}</Text>
            {l2 && <Text x={x + bw / 2} y={y + 53} size={7} anchor="middle">{l2}</Text>}
            <Text x={x + bw / 2} y={y + (l2 ? 63 : 54)} size={6.5} fill={col} anchor="middle">{r.v.toUpperCase()}</Text>
          </g>
        );
      })}
    </Frame>
  );
}

/** 04 · the lot, drawn to its own proportions. */
function LotDiagram({ rows }: { rows: SignalRow[] }) {
  const dims = rowBy(rows, /^Frontage and depth/);
  const m = /([\d.]+)\s*m\s*×\s*([\d.]+)\s*m/.exec(dims?.v ?? '');
  const area = measure(rowBy(rows, /^Land area/)?.v);
  if (!m) {
    return area
      ? <Bars rows={rows} />
      : <Empty reason="No lot dimensions are published for this address." />;
  }
  const [wM, dM] = [Number(m[1]), Number(m[2])];
  const maxPx = 104;
  const scale = maxPx / Math.max(wM, dM);
  const bw = wM * scale;
  const bh = dM * scale;
  const inner = W - PAD * 2;
  // Lot on the left, figures on the right, both placed as shares of the width
  // so the layout holds on a phone as well as on the A4 page.
  const cx = PAD + Math.max(bw / 2 + 4, inner * (narrow() ? 0.24 : 0.25));
  const tx = PAD + inner * (narrow() ? 0.52 : 0.5);
  const cy = H / 2 - 4;
  const dx = cx + bw / 2 + 12;
  return (
    <Frame>
      <rect x={cx - bw / 2} y={cy - bh / 2} width={bw} height={bh} fill={DOC_TONE.ok} opacity={0.14} stroke={DOC_TONE.ok} strokeWidth={1.4} />
      <line x1={cx - bw / 2} y1={cy + bh / 2 + 12} x2={cx + bw / 2} y2={cy + bh / 2 + 12} stroke={MUTE} />
      <Text x={cx} y={cy + bh / 2 + 25} size={8} anchor="middle">{`${wM} m frontage`}</Text>
      <line x1={dx} y1={cy - bh / 2} x2={dx} y2={cy + bh / 2} stroke={MUTE} />
      <g transform={`rotate(-90 ${dx + 10} ${cy})`}>
        <Text x={dx + 10} y={cy + 3} size={8} anchor="middle">{`${dM} m deep`}</Text>
      </g>
      <Text x={tx} y={cy - 14} fill={INK} size={20} weight={700}>{area ? `${Math.round(area.n)} m²` : '—'}</Text>
      <Text x={tx} y={cy + 4} size={8}>on the cadastral parcel</Text>
      <Text x={tx} y={cy + 20} size={8}>drawn to scale</Text>
      <Text x={tx} y={cy + 32} size={8}>{`${wM} × ${dM} m`}</Text>
    </Frame>
  );
}

/** 06 · the forecast horizons, as a curve out from what was recorded. */
function HorizonCurve({ rows }: { rows: SignalRow[] }) {
  const pts = [
    { label: 'PAST', row: rowBy(rows, /^Past growth/) },
    { label: '2 YR', row: rowBy(rows, /^2-year forecast/) },
    { label: '4 YR', row: rowBy(rows, /^4-year forecast/) },
    { label: '8 YR', row: rowBy(rows, /^8-year forecast/) },
  ]
    .map((p) => ({ ...p, n: measure(p.row?.v)?.n ?? null }))
    .filter((p) => p.n != null) as { label: string; row?: SignalRow; n: number }[];
  if (pts.length < 2) return <Bars rows={rows} />;
  // The scale spans both signs: a falling forecast (-5.3%/yr) was drawn below the frame.
  const hi = Math.max(...pts.map((p) => p.n), 0);
  const lo = Math.min(...pts.map((p) => p.n), 0);
  const span = hi - lo || 1;
  const x0 = PAD + 40;
  const x1 = W - PAD - 40;
  const y0 = 24;
  const y1 = H - 40;
  const sx = (i: number) => x0 + ((x1 - x0) * i) / (pts.length - 1);
  const sy = (v: number) => y1 - ((y1 - y0) * (v - lo)) / (span * 1.12) - (y1 - y0) * 0.06 * (lo < 0 ? 1 : 0);
  const zero = sy(0);
  const d = pts.map((p, i) => `${i ? 'L' : 'M'} ${sx(i)} ${sy(p.n)}`).join(' ');
  return (
    <Frame>
      {gridLines(4, y0, y1)}
      {lo < 0 && <line x1={x0 - 20} x2={x1 + 20} y1={zero} y2={zero} stroke={MUTE} strokeWidth={1} strokeDasharray="3 3" />}
      <path d={`${d} L ${sx(pts.length - 1)} ${zero} L ${sx(0)} ${zero} Z`} fill={DOC_TONE.strong} opacity={0.1} />
      <path d={d} fill="none" stroke={DOC_TONE.strong} strokeWidth={2} strokeLinecap="round" />
      {pts.map((p, i) => {
        const down = p.n < 0;
        const col = i === 0 ? MUTE : down ? DOC_TONE.high : DOC_TONE.strong;
        return (
          <g key={p.label}>
            <circle cx={sx(i)} cy={sy(p.n)} r={4} fill={col} />
            {/* Below a falling point, above a rising one, so the label never sits on the line. */}
            <Text x={sx(i)} y={sy(p.n) + (down ? 16 : -9)} size={8.5} fill={col} anchor="middle">{p.row?.v ?? ''}</Text>
            <Text x={sx(i)} y={H - 14} size={7.5} anchor="middle">{p.label}</Text>
          </g>
        );
      })}
      <Text x={PAD} y={H - 14} size={7.5}>% / yr</Text>
    </Frame>
  );
}

/** 07 · the suburb's bedroom bands, with this property's own marked. */
function BandLadder({ rows, midpoint }: { rows: SignalRow[]; midpoint: number | null }) {
  const bands = rowsBy(rows, /^\d+-bed .* median$/)
    .map((r) => ({ row: r, beds: Number(/^(\d+)/.exec(r.k)?.[1] ?? 0), n: measure(r.v)?.n ?? null }))
    .filter((b) => b.n != null) as { row: SignalRow; beds: number; n: number }[];
  if (bands.length < 2) return <Bars rows={rows} />;
  bands.sort((a, b) => a.beds - b.beds);
  const hi = Math.max(...bands.map((b) => b.n), midpoint ?? 0);
  const x0 = PAD + 58;
  const x1 = W - PAD - 70;
  const step = (H - 34) / bands.length;
  return (
    <Frame>
      {midpoint != null && (
        <>
          <line x1={x0 + ((x1 - x0) * midpoint) / hi} y1={10} x2={x0 + ((x1 - x0) * midpoint) / hi} y2={H - 18} stroke={DOC_TONE.high} strokeWidth={1.2} strokeDasharray="4 3" />
          <Text x={x0 + ((x1 - x0) * midpoint) / hi} y={H - 6} size={7.5} fill={DOC_TONE.high} anchor="middle">{`THIS PROPERTY ${short(midpoint)}`}</Text>
        </>
      )}
      {bands.map((b, i) => {
        const y = 16 + i * step;
        const bw = ((x1 - x0) * b.n) / hi;
        return (
          <g key={b.row.k}>
            <Text x={PAD} y={y + 8} fill={INK} size={9} weight={500}>{`${b.beds} bed`}</Text>
            <rect x={x0} y={y - 2} width={Math.max(bw, 1)} height={14} rx={3} fill={DOC_TONE.ok} opacity={0.85} />
            <Text x={x0 + bw + 8} y={y + 9} fill={DOC_TONE.ok} size={9} weight={700}>{b.row.v}</Text>
          </g>
        );
      })}
    </Frame>
  );
}

/** 08 · what the property costs to hold, as a fall from rent to cashflow. */
function CashflowWaterfall({ rows }: { rows: SignalRow[] }) {
  const items = [
    rowBy(rows, /^Net cashflow at 80/),
    rowBy(rows, /^Net cashflow at 105/),
  ]
    .map((r) => ({ row: r, n: measure(r?.v)?.n ?? null }))
    .filter((x) => x.n != null) as { row: SignalRow; n: number }[];
  if (items.length === 0) return <Bars rows={rows} />;
  const yieldRow = rowBy(rows, /^Gross yield/);
  const rates = rowBy(rows, /^Council rates/);
  const mag = Math.max(...items.map((i) => Math.abs(i.n)));
  const zero = H / 2 - 4;
  const x0 = PAD + 60;
  const bw = 78;
  return (
    <Frame>
      <line x1={PAD} y1={zero} x2={W - PAD} y2={zero} stroke={GRID} />
      <Text x={PAD} y={zero - 5} size={7.5}>BREAK EVEN</Text>
      {items.map((it, i) => {
        const h = (Math.abs(it.n) / mag) * (H / 2 - 30);
        const x = x0 + i * (bw + 34);
        const down = it.n < 0;
        return (
          <g key={it.row.k}>
            <rect x={x} y={down ? zero : zero - h} width={bw} height={h} rx={2} fill={down ? DOC_TONE.high : DOC_TONE.strong} opacity={0.85} />
            <Text x={x + bw / 2} y={down ? zero + h + 14 : zero - h - 6} size={9} weight={600} fill={down ? DOC_TONE.high : DOC_TONE.strong} anchor="middle">{it.row.v}</Text>
            <Text x={x + bw / 2} y={down ? zero + h + 24 : zero - h - 16} size={7} anchor="middle">{clip(it.row.k.replace('Net cashflow at ', ''), 18)}</Text>
          </g>
        );
      })}
      {yieldRow && <Text x={W - PAD} y={zero - 26} size={9} fill={INK} weight={600} anchor="end">{`GROSS YIELD ${yieldRow.v}`}</Text>}
      {rates && <Text x={W - PAD} y={zero - 12} size={8} anchor="end">{`COUNCIL RATES ${rates.v}`}</Text>}
    </Frame>
  );
}

/** 09 · every hazard layer checked, and what each came back as. */
function HazardMatrix({ rows }: { rows: SignalRow[] }) {
  const layers = rows.filter((r) => ['ON PROPERTY', 'WARNING', 'SAFE', 'NOT PUBLISHED'].includes(r.t));
  if (layers.length === 0) return <Bars rows={rows} />;
  const k = useContext(ChartTextScale);
  const cols = narrow() ? 3 : 6;
  const cw = (W - PAD * 2) / cols;
  const shown = layers.slice(0, 18);
  return (
    <Frame height={20 + Math.ceil(shown.length / cols) * 46}>
      {shown.map((r, i) => {
        const x = PAD + (i % cols) * cw;
        const y = 14 + Math.floor(i / cols) * 46;
        const col = r.t === 'ON PROPERTY' ? DOC_TONE.high : r.t === 'WARNING' ? DOC_TONE.warn : r.t === 'SAFE' ? DOC_TONE.strong : DOC_TONE.neutral;
        const faded = r.t === 'NOT PUBLISHED';
        return (
          <g key={r.k + i}>
            <rect x={x} y={y} width={cw - 8} height={20} rx={3} fill={col} opacity={faded ? 0.16 : 0.85} />
            <Text x={x + 4} y={y + 32} size={7} fill={INK}>{clip(r.k, fits(cw - 10, 7, k))}</Text>
            <Text x={x + 4} y={y + 40} size={6.5} fill={col}>{r.t}</Text>
          </g>
        );
      })}
    </Frame>
  );
}

/** 11 · the council's pipeline, stacked by project. */
function PipelineStack({ rows }: { rows: SignalRow[] }) {
  const items = rows
    .map((r) => ({ row: r, m: measure(r.v) }))
    .filter((x) => x.m?.unit === '$' && x.m.n > 0 && !/^Council (pipeline|rates)$/.test(x.row.k))
    .map((x) => ({ row: x.row, n: x.m!.n }))
    .sort((a, b) => b.n - a.n)
    .slice(0, 6);
  if (items.length === 0) return <Bars rows={rows} />;
  const total = items.reduce((n, i) => n + i.n, 0);
  const x0 = PAD;
  const x1 = W - PAD;
  const cols = [DOC_TONE.high, DOC_TONE.ok, DOC_TONE.cyan, DOC_TONE.pink, DOC_TONE.warn, DOC_TONE.strong];
  const k = useContext(ChartTextScale);
  let cursor = x0;
  return (
    <Frame height={86 + items.length * 13}>
      {items.map((it, i) => {
        const w = ((x1 - x0) * it.n) / total;
        const x = cursor;
        cursor += w;
        return (
          <g key={it.row.k}>
            <rect x={x} y={22} width={Math.max(w - 1.5, 1)} height={34} rx={2} fill={cols[i % cols.length]} />
            <rect x={x0} y={78 + i * 13} width={7} height={7} rx={1.5} fill={cols[i % cols.length]} />
            <Text x={x0 + 14} y={84 + i * 13} size={7.5} fill={INK}>{clip(it.row.k, fits(x1 - x0 - 14 - 64 * k, 7.5, k))}</Text>
            <Text x={x1} y={84 + i * 13} size={7.5} fill={cols[i % cols.length]} anchor="end">{it.row.v}</Text>
          </g>
        );
      })}
      <Text x={x0} y={16} size={7.5}>{`COUNCIL PIPELINE · ${items.length} PROJECTS`}</Text>
    </Frame>
  );
}

/** 13 · the suburb's age profile. */
function AgeHistogram({ rows }: { rows: SignalRow[] }) {
  const ages = rowsBy(rows, /^Aged /)
    .map((r) => ({ row: r, n: measure(r.v)?.n ?? null }))
    .filter((a) => a.n != null) as { row: SignalRow; n: number }[];
  if (ages.length < 3) return <Bars rows={rows} />;
  const hi = Math.max(...ages.map((a) => a.n));
  const bw = (W - PAD * 2) / ages.length;
  const base = H - 30;
  return (
    <Frame>
      {gridLines(4, 20, base)}
      {ages.map((a, i) => {
        const h = ((base - 22) * a.n) / hi;
        const x = PAD + i * bw;
        return (
          <g key={a.row.k}>
            <rect x={x + 2} y={base - h} width={bw - 5} height={h} rx={2} fill={DOC_TONE.ok} opacity={0.85} />
            <Text x={x + bw / 2} y={base - h - 5} size={7} fill={DOC_TONE.ok} anchor="middle">{a.row.v}</Text>
            <Text x={x + bw / 2} y={base + 12} size={6.5} anchor="middle">{a.row.k.replace('Aged ', '')}</Text>
          </g>
        );
      })}
      <Text x={W / 2} y={base + 25} size={7} anchor="middle" weight={700}>AGE (YEARS) · SHARE OF RESIDENTS</Text>
    </Frame>
  );
}

/**
 * 14 · the yield, on a scale yields are actually read against, with the rents
 * behind it. Weekly rent and an annual insurance premium are both dollars and
 * belong on different charts, so only the per-week rows sit beside the dial.
 */
const YIELD_CEILING = 8;

function YieldDial({ rows }: { rows: SignalRow[] }) {
  const y = measure(rowBy(rows, /^Gross yield/)?.v);
  const rents = rows
    .map((r) => ({ row: r, m: measure(r.v) }))
    .filter((x) => x.m?.unit === '$' && /week|\/wk|rent/i.test(`${x.row.k} ${x.row.s}`) && !/insurance|annual/i.test(x.row.k))
    .map((x) => ({ row: x.row, n: x.m!.n }))
    .slice(0, 4);

  const cx = PAD + 74;
  const cy = H / 2;
  const r = 52;
  const circumference = 2 * Math.PI * r;
  const pct = y ? Math.max(0, Math.min(1, y.n / YIELD_CEILING)) : null;
  const col = y == null ? DOC_TONE.neutral : y.n >= 3 ? DOC_TONE.strong : DOC_TONE.warn;
  const max = Math.max(...rents.map((x) => x.n), 1);

  return (
    <Frame>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={GRID} strokeWidth={9} />
      {pct != null && (
        <circle
          cx={cx} cy={cy} r={r} fill="none" stroke={col} strokeWidth={9} strokeLinecap="round"
          strokeDasharray={`${circumference * pct} ${circumference}`} transform={`rotate(-90 ${cx} ${cy})`}
        />
      )}
      <Text x={cx} y={cy + 2} fill={INK} size={20} weight={700} anchor="middle">{y ? `${y.n}%` : '—'}</Text>
      <Text x={cx} y={cy + 16} size={7} anchor="middle">{`GROSS YIELD · 0–${YIELD_CEILING}%`}</Text>
      {rents.length === 0 ? (
        <Text x={PAD + 176} y={cy} size={9}>No weekly rent is published for this address.</Text>
      ) : (
        rents.map((it, i) => {
          const yy = 34 + i * 26;
          const bx = PAD + 176;
          const bw = W - PAD - 76 - bx;
          return (
            <g key={it.row.k}>
              <Text x={bx} y={yy - 4} fill={INK} size={8.5} weight={500}>{clip(it.row.k, 32)}</Text>
              <rect x={bx} y={yy} width={bw} height={5} rx={2.5} fill={GRID} />
              <rect x={bx} y={yy} width={Math.max((bw * it.n) / max, 2)} height={5} rx={2.5} fill={DOC_TONE.ok} />
              <Text x={W - PAD - 62} y={yy + 5} fill={DOC_TONE.ok} size={8}>{it.row.v}</Text>
            </g>
          );
        })
      )}
    </Frame>
  );
}

/** What pushes prices up here and what pulls them down, either side of zero. */
function DriverDiverging({ rows }: { rows: SignalRow[] }) {
  const items = rows
    .filter((r) => r.t === 'TAILWIND' || r.t === 'HEADWIND')
    .map((r) => ({ row: r, n: measure(r.v)?.n ?? null }))
    .filter((x) => x.n != null) as { row: SignalRow; n: number }[];
  if (items.length === 0) return <Bars rows={rows} />;
  const k = useContext(ChartTextScale);
  const top = items.slice(0, 9);
  const max = Math.max(...top.map((i) => Math.abs(i.n)));
  if (narrow()) {
    // On a phone the names get a line each, over a centred axis, so a
    // negative bar's figure never runs into the name beside it.
    const midN = W / 2;
    const halfN = W / 2 - PAD - 44;
    const rowH = 27;
    const h = 14 + top.length * rowH + 10;
    return (
      <Frame height={h}>
        <line x1={midN} y1={6} x2={midN} y2={h - 16} stroke={MUTE} strokeWidth={1} />
        {top.map(({ row, n }, i) => {
          const y = 8 + i * rowH;
          const w = (halfN * Math.abs(n)) / max;
          const up = n > 0;
          const col = up ? DOC_TONE.strong : DOC_TONE.high;
          return (
            <g key={row.k + i}>
              <Text x={up ? midN + 4 : midN - 4} y={y + 7} fill={INK} size={8} weight={500} anchor={up ? 'start' : 'end'}>{clip(row.k, fits(W / 2 - PAD, 8, k))}</Text>
              <rect x={up ? midN : midN - w} y={y + 11} width={Math.max(w, 1)} height={7} rx={1.5} fill={col} />
              <Text x={up ? midN + w + 5 : midN - w - 5} y={y + 18} fill={col} size={7.5} anchor={up ? 'start' : 'end'}>{row.v}</Text>
            </g>
          );
        })}
        <Text x={midN} y={h - 4} size={7} anchor="middle">PER YEAR</Text>
      </Frame>
    );
  }
  // Names in their own column; the bars diverge about the centre of what is left,
  // each side kept short of its edge by room for the figure. Negative bars used
  // to run leftwards the full right-hand width, over the names and off the frame.
  const names = PAD + 176;
  const mid = names + (W - PAD - names) / 2;
  const half = (W - PAD - names) / 2 - 40;
  const step = (H - 22) / top.length;
  return (
    <Frame>
      <line x1={mid} y1={8} x2={mid} y2={H - 14} stroke={MUTE} strokeWidth={1} />
      {top.map(({ row, n }, i) => {
        const y = 12 + i * step;
        const w = (half * Math.abs(n)) / max;
        const up = n > 0;
        const col = up ? DOC_TONE.strong : DOC_TONE.high;
        return (
          <g key={row.k + i}>
            <Text x={PAD} y={y + 8} fill={INK} size={8.5} weight={500}>{clip(row.k, 30)}</Text>
            <rect x={up ? mid : mid - w} y={y} width={Math.max(w, 1)} height={8} rx={1.5} fill={col} />
            <Text x={up ? mid + w + 6 : mid - w - 6} y={y + 8} fill={col} size={8} anchor={up ? 'start' : 'end'}>
              {row.v}
            </Text>
          </g>
        );
      })}
      <Text x={mid} y={H - 4} size={7} anchor="middle">PER YEAR</Text>
    </Frame>
  );
}

/** 15 · what nearby property actually fetched, spread along one axis. */
function PriceStrip({ rows, midpoint }: { rows: SignalRow[]; midpoint: number | null }) {
  // Sold above the line, for sale below, each dot nudged off its neighbours so a cluster reads as a cluster.
  const pts = rows
    .map((r) => ({ m: measure(r.v), sold: r.t === 'SOLD' }))
    .filter((p) => p.m?.unit === '$' && p.m.n > 0)
    .map((p) => ({ v: p.m!.n, sold: p.sold }));
  if (pts.length < 3) return <Bars rows={rows} />;
  const lo = Math.min(...pts.map((p) => p.v));
  const hi = Math.max(...pts.map((p) => p.v));
  const x0 = PAD + 40;
  const x1 = W - PAD - 40;
  const y = 92;
  const sx = (v: number) => x0 + ((x1 - x0) * (v - lo)) / (hi - lo || 1);
  const R = 5.5;
  const placed: { x: number; y: number; sold: boolean }[] = [];
  for (const p of [...pts].sort((a, b) => a.v - b.v)) {
    const x = sx(p.v);
    let k = 0;
    const dir = p.sold ? -1 : 1;
    while (placed.some((q) => q.sold === p.sold && Math.abs(q.x - x) < R * 2 && Math.abs(q.y - (y + dir * (R + 4 + k * R * 2))) < R * 2)) k++;
    placed.push({ x, y: y + dir * (R + 4 + Math.min(k, 3) * R * 2), sold: p.sold });
  }
  const sold = pts.filter((p) => p.sold).map((p) => p.v).sort((a, b) => a - b);
  const median = sold.length ? sold[Math.floor(sold.length / 2)] : null;
  const nSold = sold.length;
  const nList = pts.length - nSold;
  return (
    <Frame height={190}>
      <circle cx={PAD + 5} cy={16} r={4.5} fill={DOC_TONE.ok} />
      <Text x={PAD + 14} y={19} size={7.5}>{`SOLD · ${nSold}`}</Text>
      <circle cx={PAD + 82} cy={16} r={4.5} fill={DOC_TONE.pink} />
      <Text x={PAD + 91} y={19} size={7.5}>{`FOR SALE · ${nList}`}</Text>
      <line x1={x0} y1={y} x2={x1} y2={y} stroke={GRID} strokeWidth={1.5} />
      {placed.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={R} fill={p.sold ? DOC_TONE.ok : DOC_TONE.pink} opacity={0.75} stroke="#FFFFFF" strokeWidth={1} />
      ))}
      {median != null && (
        <>
          <line x1={sx(median)} y1={y - 48} x2={sx(median)} y2={y} stroke={DOC_TONE.ok} strokeWidth={1.5} />
          <Text x={sx(median)} y={y - 53} size={8} fill={DOC_TONE.ok} anchor="middle">{`MEDIAN SOLD ${short(median)}`}</Text>
        </>
      )}
      {midpoint != null && midpoint >= lo && midpoint <= hi && (
        <>
          <line x1={sx(midpoint)} y1={y} x2={sx(midpoint)} y2={y + 48} stroke={DOC_TONE.high} strokeWidth={1.5} strokeDasharray="3 3" />
          <Text x={sx(midpoint)} y={y + 60} size={8} fill={DOC_TONE.high} anchor="middle">{`THIS PROPERTY ${short(midpoint)}`}</Text>
        </>
      )}
      <Text x={x0} y={y + 78} size={7.5} anchor="middle">{short(lo)}</Text>
      <Text x={x1} y={y + 78} size={7.5} anchor="middle">{short(hi)}</Text>
    </Frame>
  );
}

/** 16 · the model's band against the figure the sales support. */
function BandLine({ rows, midpoint }: { rows: SignalRow[]; midpoint: number | null }) {
  const predicted = measure(rowBy(rows, /^Predicted price/)?.v)?.n ?? null;
  const band = /\$([\d,]+)\s*[–-]\s*\$([\d,]+)/.exec(rowBy(rows, /^Band$/)?.v ?? '');
  if (predicted == null || !band) return <Bars rows={rows} />;
  const lo = Number(band[1].replace(/,/g, ''));
  const hi = Number(band[2].replace(/,/g, ''));
  const min = Math.min(lo, midpoint ?? lo) * 0.96;
  const max = Math.max(hi, midpoint ?? hi) * 1.04;
  const x0 = PAD + 30;
  const x1 = W - PAD - 30;
  const y = H / 2;
  const sx = (v: number) => x0 + ((x1 - x0) * (v - min)) / (max - min || 1);
  return (
    <Frame>
      <line x1={x0} y1={y} x2={x1} y2={y} stroke={GRID} />
      <rect x={sx(lo)} y={y - 13} width={sx(hi) - sx(lo)} height={26} rx={4} fill={DOC_TONE.neutral} opacity={0.16} />
      <Text x={sx(lo)} y={y + 34} size={7.5}>{short(lo)}</Text>
      <Text x={sx(hi)} y={y + 34} size={7.5} anchor="end">{short(hi)}</Text>
      <Text x={(sx(lo) + sx(hi)) / 2} y={y + 46} size={7.5} anchor="middle">THE MODEL’S OWN BAND</Text>
      <circle cx={sx(predicted)} cy={y} r={6} fill={DOC_TONE.neutral} />
      <Text x={sx(predicted)} y={y - 20} size={9} weight={600} fill={DOC_TONE.neutral} anchor="middle">{short(predicted)}</Text>
      <Text x={sx(predicted)} y={y - 31} size={7} anchor="middle">ESTIMATE</Text>
      {midpoint != null && (
        <>
          <line x1={sx(midpoint)} y1={y - 30} x2={sx(midpoint)} y2={y + 18} stroke={DOC_TONE.high} strokeWidth={2} />
          <Text x={sx(midpoint)} y={y - 36} size={9} weight={600} fill={DOC_TONE.high} anchor="middle">{short(midpoint)}</Text>
          <Text x={sx(midpoint)} y={y + 30} size={7} fill={DOC_TONE.high} anchor="middle">WHAT THE SALES SUPPORT</Text>
        </>
      )}
    </Frame>
  );
}

/** 17 · schools by how far away they are. */
function SchoolRings({ rows }: { rows: SignalRow[] }) {
  const schools = rows
    .map((r) => {
      const m = /·\s*([\d,]+)\s*m\s*·/.exec(r.s ?? '');
      return m ? { row: r, metres: Number(m[1].replace(/,/g, '')) } : null;
    })
    .filter((x): x is { row: SignalRow; metres: number } => x != null)
    .sort((a, b) => a.metres - b.metres)
    .slice(0, 7);
  if (schools.length === 0) return <Bars rows={rows} />;
  const k = useContext(ChartTextScale);
  const tight = narrow();
  const maxM = Math.max(...schools.map((s) => s.metres));
  const maxR = tight ? 54 : 62;
  const cx = PAD + (tight ? maxR + 2 : 82);
  const cy = H / 2;
  const lx = tight ? cx + maxR + 18 : PAD + 190;
  // The list is centred on the rings, top to bottom, rather than hanging from the top edge.
  const rowH = tight ? 22 : 18;
  const top = cy - (schools.length * rowH) / 2;
  return (
    <Frame>
      {[0.33, 0.66, 1].map((f) => (
        <circle key={f} cx={cx} cy={cy} r={maxR * f} fill="none" stroke={GRID} />
      ))}
      <circle cx={cx} cy={cy} r={3} fill={DOC_TONE.high} />
      {schools.map((s, i) => {
        const a = (Math.PI * 2 * i) / schools.length - Math.PI / 2;
        const r = (maxR * s.metres) / maxM;
        return <circle key={s.row.k} cx={cx + Math.cos(a) * r} cy={cy + Math.sin(a) * r} r={4.5} fill={DOC_TONE.ok} opacity={0.85} />;
      })}
      <Text x={cx} y={cy + maxR + 16} size={7} anchor="middle">{`${maxM} m`}</Text>
      {schools.map((s, i) =>
        tight ? (
          // Name, then its distance and rank beneath — side by side they collide.
          <g key={`l${s.row.k}`}>
            <Text x={lx} y={top + 9 + i * rowH} size={8.5} fill={INK} weight={500}>{clip(s.row.k, fits(W - PAD - lx, 8.5, k))}</Text>
            <Text x={lx} y={top + 18 + i * rowH} size={7.5} fill={DOC_TONE.ok}>{`${s.metres} m · ${s.row.v}`}</Text>
          </g>
        ) : (
          <g key={`l${s.row.k}`}>
            <Text x={lx} y={top + 12 + i * rowH} size={8.5} fill={INK} weight={500}>{clip(s.row.k, 40)}</Text>
            <Text x={W - PAD} y={top + 12 + i * rowH} size={8} fill={DOC_TONE.ok} anchor="end">{`${s.metres} m · ${s.row.v}`}</Text>
          </g>
        ),
      )}
    </Frame>
  );
}

/** 18 · what the council publishes at this parcel, and what it does not. */
function PublishedGrid({ rows }: { rows: SignalRow[] }) {
  if (rows.length === 0) return <Empty reason="Nothing is published for this parcel." />;
  const k = useContext(ChartTextScale);
  const cols = narrow() ? 2 : 3;
  const cw = (W - PAD * 2) / cols;
  const shown = rows.slice(0, 12);
  return (
    <Frame height={16 + Math.ceil(shown.length / cols) * 44}>
      {shown.map((r, i) => {
        const has = r.v !== '—' && r.v !== '';
        const x = PAD + (i % cols) * cw;
        const y = 12 + Math.floor(i / cols) * 44;
        return (
          <g key={r.k + i}>
            <rect x={x} y={y} width={cw - 10} height={28} rx={4} fill={has ? DOC_TONE.ok : DOC_TONE.neutral} opacity={has ? 0.14 : 0.07} stroke={has ? DOC_TONE.ok : GRID} strokeWidth={1} />
            <Text x={x + 8} y={y + 12} size={7} fill={MUTE}>{clip(r.k, fits(cw - 26, 7, k))}</Text>
            <Text x={x + 8} y={y + 23} size={8.5} fill={has ? INK : MUTE} weight={600}>{clip(r.v, fits(cw - 26, 8.5, k))}</Text>
          </g>
        );
      })}
    </Frame>
  );
}

/** 19 · this suburb against the national median, offence by offence. */
function CrimeBars({ rows }: { rows: SignalRow[] }) {
  const items = rows
    .map((r) => {
      const here = measure(r.v)?.n ?? null;
      const nat = /national median ([\d,]+)/.exec(r.s ?? '');
      return here != null && nat ? { row: r, here, nat: Number(nat[1].replace(/,/g, '')) } : null;
    })
    .filter((x): x is { row: SignalRow; here: number; nat: number } => x != null)
    .slice(0, 8);
  if (items.length === 0) return <Bars rows={rows} />;
  const max = Math.max(...items.flatMap((i) => [i.here, i.nat]));
  const x0 = PAD + 96;
  const x1 = W - PAD - 40;
  const step = (H - 26) / items.length;
  return (
    <Frame>
      {items.map((it, i) => {
        const y = 14 + i * step;
        const wh = ((x1 - x0) * it.here) / max;
        const wn = ((x1 - x0) * it.nat) / max;
        return (
          <g key={it.row.k}>
            <Text x={PAD} y={y + 9} fill={INK} size={8.5} weight={500}>{clip(it.row.k, 16)}</Text>
            <rect x={x0} y={y} width={Math.max(wn, 1)} height={4} rx={2} fill={MUTE} opacity={0.4} />
            <rect x={x0} y={y + 6} width={Math.max(wh, 1)} height={6} rx={2} fill={it.here <= it.nat ? DOC_TONE.strong : DOC_TONE.high} />
            <Text x={x0 + Math.max(wh, wn) + 8} y={y + 10} size={8} fill={it.here <= it.nat ? DOC_TONE.strong : DOC_TONE.high}>{it.row.v}</Text>
          </g>
        );
      })}
      <Text x={PAD} y={H - 4} size={7}>THIS SUBURB</Text>
      <Text x={PAD + 84} y={H - 4} size={7} fill={MUTE}>NATIONAL MEDIAN</Text>
    </Frame>
  );
}

/** 20 · how long the standing stock would take to clear. */
function SupplyMeter({ rows }: { rows: SignalRow[] }) {
  const months = measure(rowBy(rows, /^Months of supply/)?.v)?.n ?? null;
  if (months == null) return <Bars rows={rows} />;
  const x0 = PAD + 20;
  const x1 = W - PAD - 20;
  const y = 70;
  const cap = 9;
  const sx = (m: number) => x0 + ((x1 - x0) * Math.min(m, cap)) / cap;
  const zones: [number, number, string, string][] = [
    [0, 3, DOC_TONE.high, "SELLER'S MARKET"],
    [3, 6, DOC_TONE.warn, 'BALANCED'],
    [6, cap, DOC_TONE.strong, "BUYER'S MARKET"],
  ];
  return (
    <Frame height={150}>
      {zones.map(([a, b, col, label]) => (
        <g key={label}>
          <rect x={sx(a)} y={y - 12} width={sx(b) - sx(a) - 2} height={24} rx={3} fill={col} opacity={0.42} />
          <Text x={(sx(a) + sx(b)) / 2} y={y + 30} size={7} fill={col} anchor="middle">{label}</Text>
        </g>
      ))}
      <line x1={sx(months)} y1={y - 26} x2={sx(months)} y2={y + 16} stroke={INK} strokeWidth={2} />
      <Text x={sx(months)} y={y - 32} size={12} weight={700} fill={INK} anchor="middle">{`${months.toFixed(2)} MONTHS`}</Text>
      {[0, 3, 6, 9].map((m) => (
        <Text key={m} x={sx(m)} y={y + 46} size={7} anchor="middle">{String(m)}</Text>
      ))}
      <Text x={x0} y={y + 62} size={7.5}>MONTHS TO CLEAR THE STANDING STOCK</Text>
    </Frame>
  );
}

/** 21 · the suburb's ancestries, stacked. Shares exceed 100% by design. */
function AncestryStack({ rows }: { rows: SignalRow[] }) {
  const items = rows
    .filter((r) => /ancestry, census/.test(r.s ?? ''))
    .map((r) => ({ row: r, n: measure(r.v)?.n ?? null }))
    .filter((x) => x.n != null)
    .slice(0, 8) as { row: SignalRow; n: number }[];
  if (items.length < 2) return <Bars rows={rows} />;
  const total = items.reduce((n, i) => n + i.n, 0);
  const cols = [DOC_TONE.high, DOC_TONE.ok, DOC_TONE.cyan, DOC_TONE.pink, DOC_TONE.warn, DOC_TONE.strong, DOC_TONE.neutral, '#8A6BD9'];
  let cursor = PAD;
  const width = W - PAD * 2;
  return (
    <Frame height={150}>
      {items.map((it, i) => {
        const w = (width * it.n) / total;
        const x = cursor;
        cursor += w;
        return (
          <g key={it.row.k}>
            <rect x={x} y={20} width={Math.max(w - 1.5, 1)} height={32} rx={2} fill={cols[i % cols.length]} />
            <rect x={PAD + (i % 4) * 140} y={74 + Math.floor(i / 4) * 16} width={7} height={7} rx={1.5} fill={cols[i % cols.length]} />
            <Text x={PAD + 12 + (i % 4) * 140} y={80 + Math.floor(i / 4) * 16} size={7.5} fill={INK}>{`${clip(it.row.k, 12)} ${it.row.v}`}</Text>
          </g>
        );
      })}
      <Text x={PAD} y={14} size={7.5}>TOP ANCESTRIES · UP TO TWO RECORDED PER PERSON</Text>
    </Frame>
  );
}

/** 22 · the suburbs that behave like this one. */
function MatchStrip({ rows }: { rows: SignalRow[] }) {
  // Only the matched suburbs — not the area's population, dwellings or neighbours.
  const names = rows.filter((r) => r.v === 'Comparable').slice(0, 18);
  if (names.length === 0) return <Bars rows={rows} />;
  const k = useContext(ChartTextScale);
  const cols = narrow() ? 2 : 3;
  const cw = (W - PAD * 2) / cols;
  return (
    <Frame height={18 + Math.ceil(names.length / cols) * 22}>
      {names.map((r, i) => {
        const x = PAD + (i % cols) * cw;
        const y = 18 + Math.floor(i / cols) * 22;
        return (
          <g key={r.k + i}>
            <rect x={x} y={y - 9} width={cw - 12} height={16} rx={8} fill={DOC_TONE.cyan} opacity={0.14} />
            <Text x={x + 10} y={y + 2} size={8} fill={INK}>{clip(r.k, fits(cw - 30, 8, k))}</Text>
          </g>
        );
      })}
    </Frame>
  );
}

// ── Dispatch ───────────────────────────────────────────────────────────────

export function ChartFor({ module, midpoint, width = 600 }: { module: Module; midpoint: number | null; width?: number }) {
  W = width;
  const rows = module.rows;
  switch (module.key) {
    case 'your-position':
      return <Gauge module={module} />;
    case 'rental-signals':
      return <YieldDial rows={rows} />;
    case 'buyer-fit':
      return <FitBlocks rows={rows} />;
    case 'property-profile':
      return <LotDiagram rows={rows} />;
    case 'comparable-sales':
      return <Scatter rows={rows} midpoint={midpoint} />;
    case 'street-forecast':
      return <HorizonCurve rows={rows} />;
    case 'suburb-benchmark':
      return <BandLadder rows={rows} midpoint={midpoint} />;
    case 'budget-exposure':
      return <CashflowWaterfall rows={rows} />;
    case 'risks':
      return <HazardMatrix rows={rows} />;
    case 'growth-drivers':
      return <DriverDiverging rows={rows} />;
    case 'market-signals':
      // A suburb's price drivers are percentages, counts and a median in
      // dollars — not a series. Drawn as one, unrelated percentages were
      // joined into a line that meant nothing; bars of one unit say what they are.
      return <Bars rows={rows} />;
    case 'development':
      return <PipelineStack rows={rows} />;
    case 'lifestyle':
      return <Radar rows={rows} />;
    case 'demographics':
      return <AgeHistogram rows={rows} />;
    case 'properties-nearby':
      return <PriceStrip rows={rows} midpoint={midpoint} />;
    case 'valuation-avm':
      return <BandLine rows={rows} midpoint={midpoint} />;
    case 'schools':
      return <SchoolRings rows={rows} />;
    case 'planning-zoning':
      return <PublishedGrid rows={rows} />;
    case 'crime':
      return <CrimeBars rows={rows} />;
    case 'supply-demand':
      return <SupplyMeter rows={rows} />;
    case 'ethnicity':
      return <AncestryStack rows={rows} />;
    case 'similar-suburbs':
      return <MatchStrip rows={rows} />;
    default:
      return <Bars rows={rows} />;
  }
}
